import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import React, {useEffect, useState} from 'react';
import TextInput from 'components/inputs/TextInput';
import {BillingSolution} from 'types/billingSolution';
import SubmitButton from 'components/SubmitButton';
import {BillingSolutionThunks} from 'features/programs/program/billingSolution/billingSolutionThunks';
import {setIsEditing} from 'features/programs/program/billingSolution/billingSolutionSlice';
import CustomSelect from 'components/inputs/CustomSelect';
import XIcon from 'assets/images/icons/XIcon';
import XCircleIcon from 'assets/images/icons/XCircleIcon';
import {BillingSolutionRange} from 'types/billingSolutionRange';

interface BillingSolutionDrawerProps {
  id?: string;
  closeDrawer: () => void;
}

const BillingSolutionDrawer: React.FC<BillingSolutionDrawerProps> = ({
  closeDrawer,
}) => {
  const billingSolution = useSelector(
    (state: RootState) => state.billingSolution.billingSolution,
  );
  const dispatch = useDispatch<AppDispatch>();
  const [updatedBillingSolution, setUpdatedBillingSolution] = useState({
    ...billingSolution,
  });
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const claimsByOptions = [
    {value: 'note', label: 'Note'},
    {value: 'daily', label: 'Daily'},
    {value: 'weekly', label: 'Weekly'},
    {value: 'monthly', label: 'Monthly'},
  ];

  const unitsByOptions = [
    {value: 'note', label: 'Note'},
    {value: 'day', label: 'Day'},
    {value: 'period', label: 'Period'},
  ];

  const conversionTypeOptions = [
    {value: 'ratio', label: 'Ratio'},
    {value: 'ratio_partial', label: 'Ratio Partial Unit'},
    {value: 'range', label: 'Range'},
  ];
  const partialUnitOptions = [
    {value: 'whole_unit', label: 'Whole Unit'},
    {value: 'half_unit', label: 'Half Unit'},
    {value: 'quarter_unit', label: 'Quarter Unit'},
  ];

  useEffect(() => {
    const isFormValid =
      updatedBillingSolution.name &&
      updatedBillingSolution.createClaimsBy &&
      updatedBillingSolution.totalUnitsBy &&
      updatedBillingSolution.unitConversionType;
    let additionalValidation = true;

    if (
      updatedBillingSolution.unitConversionType === 'ratio' ||
      updatedBillingSolution.unitConversionType === 'ratio_partial'
    ) {
      additionalValidation =
        updatedBillingSolution.unitConversionMinutes > 0 &&
        updatedBillingSolution.unitConversionUnits > 0;
    } else if (updatedBillingSolution.unitConversionType === 'range') {
      additionalValidation =
        updatedBillingSolution.billingSolutionRanges &&
        updatedBillingSolution.billingSolutionRanges.length > 0 &&
        updatedBillingSolution.billingSolutionRanges.some(
          (range: any) =>
            !range._destroy &&
            range.min !== '' &&
            range.max !== '' &&
            range.units !== '',
        );
    }

    setIsSaveDisabled(!isFormValid || !additionalValidation);
  }, [
    updatedBillingSolution.name,
    updatedBillingSolution.createClaimsBy,
    updatedBillingSolution.totalUnitsBy,
    updatedBillingSolution.unitConversionType,
    updatedBillingSolution.unitConversionMinutes,
    updatedBillingSolution.unitConversionUnits,
    updatedBillingSolution.billingSolutionRanges,
  ]);

  useEffect(() => {
    const billingSolutionWithRanges = {
      ...billingSolution,
      billingSolutionRanges:
        billingSolution.billingSolutionRanges?.map(
          (range: any) => new BillingSolutionRange(range),
        ) || [],
    };
    setUpdatedBillingSolution(billingSolutionWithRanges);
  }, [billingSolution]);

  const handleChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | {target: {id: string; value: any}},
  ) => {
    const {id, value} = e.target;
    setUpdatedBillingSolution({...updatedBillingSolution, [id]: value});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const billingSolutionToSend = new BillingSolution(updatedBillingSolution);

    if (billingSolution?.id) {
      await dispatch(BillingSolutionThunks.update(billingSolutionToSend)).then(
        () => closeDrawer(),
      );
    } else {
      await dispatch(BillingSolutionThunks.create(billingSolutionToSend)).then(
        () => closeDrawer(),
      );
    }
  };

  const handleClose = () => {
    dispatch(setIsEditing(false));
    closeDrawer();
  };

  const handleRangeChange = (e: any, index: any, field: string) => {
    const {value} = e.target;
    const updatedRanges = [
      ...(updatedBillingSolution.billingSolutionRanges || []),
    ];
    const existingRange = updatedRanges[index];
    updatedRanges[index] = new BillingSolutionRange({
      ...existingRange,
      [field]: value,
    });
    setUpdatedBillingSolution({
      ...updatedBillingSolution,
      billingSolutionRanges: updatedRanges,
    });
  };

  const addNewRange = () => {
    const newRange = new BillingSolutionRange({
      min: 0,
      max: 0,
      units: 0,
      _destroy: false,
    });

    const updatedRanges = [
      ...(updatedBillingSolution.billingSolutionRanges || []),
      newRange,
    ];
    setUpdatedBillingSolution({
      ...updatedBillingSolution,
      billingSolutionRanges: updatedRanges,
    });
  };

  const removeRange = (index: any) => {
    const updatedRanges = [
      ...(updatedBillingSolution.billingSolutionRanges || []),
    ];
    const range = updatedRanges[index];

    if (range.id && range.id > 0) {
      updatedRanges[index] = new BillingSolutionRange({
        ...range,
        _destroy: true,
      });
    } else {
      updatedRanges.splice(index, 1);
    }
    setUpdatedBillingSolution({
      ...updatedBillingSolution,
      billingSolutionRanges: updatedRanges,
    });
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex flex-col">
        <div className="h-auto p-6 bg-sky-50 border-b border-sky-200 flex justify-between items-start gap-2 w-full">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-lg font-semibold tracking-normal text-cyan-800">
              {billingSolution?.id
                ? 'Edit billing solution'
                : 'Create new billing solution'}
            </div>
            <div className="text-sm font-light tracking-normal leading-5 text-zinc-400">
              {billingSolution?.id ? 'Edit ' : 'Create a new '}billing solution
              and set its parameters
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
            <XIcon />
          </button>
        </div>
        <div className="flex-1 px-3 pb-6 pt-3 overflow-y-auto">
          <div className="h-full flex flex-col overflow-y-auto overflow-hidden">
            <div className="h-full px-4">
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col space-y-6 items-start mt-4">
                  <div className="w-full">
                    <TextInput
                      id="name"
                      type="text"
                      label="Name"
                      value={updatedBillingSolution?.name || ''}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="w-full flex gap-4">
                    <div className="w-1/2">
                      <div className="flex flex-col gap-[0.625rem] w-full">
                        <label
                          htmlFor="createClaimsBy"
                          className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                          Create Claims By
                        </label>
                        <CustomSelect
                          options={claimsByOptions}
                          value={updatedBillingSolution.createClaimsBy}
                          onChange={selectedOption =>
                            handleChange({
                              target: {
                                id: 'createClaimsBy',
                                value: selectedOption.value,
                              },
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="w-1/2">
                      <div className="flex flex-col gap-[0.625rem] w-full">
                        <label
                          htmlFor="totalUnitsBy"
                          className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                          Total Units By
                        </label>
                        <CustomSelect
                          options={unitsByOptions}
                          value={updatedBillingSolution.totalUnitsBy}
                          onChange={selectedOption =>
                            handleChange({
                              target: {
                                id: 'totalUnitsBy',
                                value: selectedOption.value,
                              },
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="w-full">
                    <div className="flex flex-col gap-[0.625rem] w-full">
                      <label
                        htmlFor="unitConversionType"
                        className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                        Unit Conversion Type
                      </label>
                      <CustomSelect
                        options={conversionTypeOptions}
                        value={updatedBillingSolution.unitConversionType}
                        onChange={selectedOption =>
                          handleChange({
                            target: {
                              id: 'unitConversionType',
                              value: selectedOption.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  {updatedBillingSolution.unitConversionType &&
                    updatedBillingSolution.unitConversionType !== 'none' && (
                      <>
                        {updatedBillingSolution.unitConversionType ===
                          'ratio' && (
                          <div className="w-full flex gap-4">
                            <div className="w-1/2">
                              <TextInput
                                id="unitConversionMinutes"
                                type="number"
                                label="Minutes"
                                value={
                                  updatedBillingSolution?.unitConversionMinutes ||
                                  ''
                                }
                                placeholder="0"
                                onChange={handleChange}
                                min="0"
                              />
                            </div>
                            <div className="w-1/2">
                              <TextInput
                                id="unitConversionUnits"
                                type="number"
                                label="Units"
                                value={
                                  updatedBillingSolution?.unitConversionUnits ||
                                  ''
                                }
                                placeholder="0"
                                onChange={handleChange}
                                min="0"
                              />
                            </div>
                          </div>
                        )}
                        {updatedBillingSolution.unitConversionType ===
                          'ratio_partial_unit' && (
                          <div className="w-full flex gap-4">
                            <div className="w-1/2">
                              <TextInput
                                id="unitConversionMinutes"
                                type="number"
                                label="Minutes"
                                value={
                                  updatedBillingSolution?.unitConversionMinutes ||
                                  ''
                                }
                                placeholder="0"
                                onChange={handleChange}
                                min="0"
                              />
                            </div>
                            <div className="w-1/2">
                              <div className="flex flex-col gap-[0.625rem] w-full">
                                <label
                                  htmlFor="unitConversionPartialUnit"
                                  className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                                  Partial Unit
                                </label>
                                <CustomSelect
                                  options={partialUnitOptions}
                                  value={
                                    updatedBillingSolution.unitConversionUnitType
                                  }
                                  onChange={selectedOption =>
                                    handleChange({
                                      target: {
                                        id: 'unitConversionUnitType',
                                        value: selectedOption.value,
                                      },
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {updatedBillingSolution.unitConversionType ===
                          'range' && (
                          <>
                            {updatedBillingSolution.billingSolutionRanges
                              ?.filter(
                                (range: BillingSolutionRange) =>
                                  !range._destroy,
                              )
                              ?.map(
                                (range: BillingSolutionRange, index: any) => (
                                  <div
                                    key={range.id || `new-${index}`}
                                    className="w-full flex items-center gap-2 mb-4">
                                    <div className="w-16">
                                      <TextInput
                                        label={''}
                                        id="rangeStart"
                                        type="number"
                                        placeholder="0"
                                        value={range.min.toString() || ''}
                                        onChange={e =>
                                          handleRangeChange(e, index, 'min')
                                        }
                                        min="0"
                                      />
                                    </div>
                                    <span className="text-xs font-normal">
                                      up to
                                    </span>
                                    <div className="w-16">
                                      <TextInput
                                        label={''}
                                        id="rangeEnd"
                                        type="number"
                                        placeholder="0"
                                        value={range.max.toString() || ''}
                                        onChange={e =>
                                          handleRangeChange(e, index, 'max')
                                        }
                                        min="0"
                                      />
                                    </div>
                                    <span className="text-xs font-normal">
                                      hours =
                                    </span>
                                    <div className="w-16">
                                      <TextInput
                                        label={''}
                                        id="rangeUnits"
                                        type="number"
                                        placeholder="0"
                                        value={range.units.toString() || ''}
                                        onChange={e =>
                                          handleRangeChange(e, index, 'units')
                                        }
                                        min="0"
                                      />
                                    </div>
                                    <span className="text-xs font-normal">
                                      units
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => removeRange(index)}
                                      className="text-slate-400 hover:text-slate-600 font-semibold">
                                      <XCircleIcon />
                                    </button>
                                  </div>
                                ),
                              )}
                            <button
                              type="button"
                              onClick={addNewRange}
                              className="text-cyan-400 hover:text-cyan-500  text-sm font-medium ">
                              + Add Range
                            </button>
                          </>
                        )}
                      </>
                    )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <footer className="flex-shrink-0 px-6 pb-4 pt-5 flex justify-end gap-5">
        <div className="border-t border-slate-200 w-full">
          <div className="flex w-full justify-center items-center gap-x-2 pb-4 pt-4">
            <SubmitButton
              onClick={handleSubmit}
              label={
                (billingSolution?.id ? 'Save' : 'Create new') +
                ' billing solution'
              }
              disabled={isSaveDisabled}
            />
          </div>
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <button
              type="button"
              onClick={handleClose}
              className="self-center text-sm font-light tracking-normal leading-5 text-center text-zinc-400 hover:text-cyan-400">
              Never mind
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BillingSolutionDrawer;
